import { useMemo, useCallback, useEffect, useState, useRef } from "react";
import {
  useConnect,
  Connector,
  useAccount,
  useDisconnect,
  useReconnect,
  ConnectorAlreadyConnectedError,
} from "wagmi";
import { getAccount, watchAccount } from "@wagmi/core";
import { ChainDetailType } from "../../../interfaces/environment";
import { chains as wagmiChain, config as wagmiConfig } from "../../../../wagmi.config";
import { UserRejectedRequestError } from "viem";
import { safeWeb3Operation } from "../../../utils/errorHandler";

const syncAddressFromConnector = async (
  connector: Connector | undefined
): Promise<`0x${string}` | undefined> => {
  if (!connector) return undefined;

  try {
    const accounts = await connector.getAccounts();
    return accounts[0];
  } catch {
    return undefined;
  }
};

const useEthWalletConnection = (chain: ChainDetailType.Item) => {
  const { connectAsync, connectors } = useConnect();
  const addressRef = useRef<`0x${string}` | undefined>(undefined);
  const [connectedAddress, setConnectedAddress] = useState<
    `0x${string}` | undefined
  >(undefined);
  const [isConnecting, setIsConnecting] = useState(false);

  const { disconnectAsync } = useDisconnect();
  const { reconnect } = useReconnect();
  const {
    address: wagmiAddress,
    connector: currentConnector,
    isConnected: wagmiIsConnected,
    status: accountStatus,
  } = useAccount();

  const wallets = useMemo(() => {
    return connectors.map((connector: Connector) => connector.name);
  }, [connectors]);

  const applyAddress = useCallback((address: `0x${string}` | undefined) => {
    addressRef.current = address;
    setConnectedAddress(address);
  }, []);

  const setAddress = useCallback(
    (address: `0x${string}`) => {
      applyAddress(address);
    },
    [applyAddress]
  );

  const getConnectorAddress = useCallback(async () => {
    return syncAddressFromConnector(currentConnector);
  }, [currentConnector]);

  const syncWalletState = useCallback(async () => {
    const account = getAccount(wagmiConfig);
    if (account.address) {
      applyAddress(account.address);
      return account.address;
    }

    const connectorAddress = await syncAddressFromConnector(
      account.connector ?? currentConnector
    );
    if (connectorAddress) {
      applyAddress(connectorAddress);
    }
    return connectorAddress;
  }, [applyAddress, currentConnector]);

  useEffect(() => {
    if (wagmiAddress) {
      applyAddress(wagmiAddress);
      return;
    }

    // Only clear after wagmi reports a real disconnect.
    // Do not clear while WalletConnect is still syncing (wagmi lags behind the connector).
    if (accountStatus === "disconnected") {
      applyAddress(undefined);
    }
  }, [wagmiAddress, accountStatus, applyAddress]);

  useEffect(() => {
    const unwatch = watchAccount(wagmiConfig, {
      onChange(account) {
        if (account.address) {
          applyAddress(account.address);
          return;
        }

        if (account.status === "disconnected") {
          applyAddress(undefined);
        }
      },
    });

    return unwatch;
  }, [applyAddress]);

  useEffect(() => {
    const handleAppVisible = () => {
      if (document.visibilityState !== "visible") return;
      reconnect();
      void syncWalletState();
    };

    document.addEventListener("visibilitychange", handleAppVisible);
    window.addEventListener("focus", handleAppVisible);
    return () => {
      document.removeEventListener("visibilitychange", handleAppVisible);
      window.removeEventListener("focus", handleAppVisible);
    };
  }, [reconnect, syncWalletState]);

  useEffect(() => {
    const walletConnectConnector = connectors.find(
      (connector) => connector.id === "walletConnect"
    );
    if (!walletConnectConnector) return;

    let disposed = false;
    let provider: any = null;

    const handleProviderConnect = async () => {
      await reconnect();
      await syncWalletState();
    };

    const handleAccountsChanged = (accounts: string[]) => {
      if (accounts[0]) {
        applyAddress(accounts[0] as `0x${string}`);
      }
    };

    void (async () => {
      try {
        provider = await walletConnectConnector.getProvider();
        if (!provider || disposed) return;

        provider.on("connect", handleProviderConnect);
        provider.on("accountsChanged", handleAccountsChanged);

        if (provider.session) {
          await handleProviderConnect();
        }
      } catch (error) {
        console.warn("WalletConnect provider listener setup failed:", error);
      }
    })();

    return () => {
      disposed = true;
      provider?.removeListener("connect", handleProviderConnect);
      provider?.removeListener("accountsChanged", handleAccountsChanged);
    };
  }, [connectors, reconnect, syncWalletState, applyAddress]);

  useEffect(() => {
    if (connectedAddress) return;

    const intervalId = window.setInterval(() => {
      void syncWalletState();
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [connectedAddress, syncWalletState]);

  const disconnect = useCallback(async () => {
    if (currentConnector) {
      await disconnectAsync({ connector: currentConnector });
      applyAddress(undefined);
    } else {
      console.error("useEthWalletConnection: disconnect, Connector not found");
    }
  }, [currentConnector, disconnectAsync, applyAddress]);

  const switchChain = useCallback(
    async (chainId: number, connector?: Connector) => {
      const chain = wagmiChain.find((chain) => chain.id === chainId);
      if (!chain) {
        throw new Error("Chain not found");
      }

      const _connector = currentConnector || connector;
      if (!_connector) {
        throw new Error("Connector not found");
      }

      if (!_connector.switchChain) {
        throw new Error("Connector does not support switchChain");
      }

      const payload = {
        chainId: chainId,
        addEthereumChainParameter: {
          chainName: chain.name,
          nativeCurrency: {
            name: chain.nativeCurrency.name,
            symbol: chain.nativeCurrency.symbol,
            decimals: chain.nativeCurrency.decimals,
          },
          rpcUrls: chain.rpcUrls.default.http,
          blockExplorerUrls: chain.blockExplorers
            ? [chain.blockExplorers.default.url]
            : undefined,
          iconUrls: [],
        },
      };

      try {
        // At this point, _connector is guaranteed to exist and have switchChain
        const res = await _connector.switchChain(payload);
        return !!res?.id;
      } catch (error) {
        // Handle network failures gracefully
        console.warn("Chain switching failed:", error);
        return false;
      }
    },
    [currentConnector]
  );

  const connect = useCallback(
    async (walletName: string) => {
      if (isConnecting) {
        return connectedAddress ?? addressRef.current;
      }

      const connector = connectors.find(
        (connector: Connector) => connector.name === walletName
      );
      if (connector) {
        if (
          wagmiIsConnected &&
          currentConnector?.name === walletName &&
          wagmiAddress
        ) {
          setAddress(wagmiAddress);
          return wagmiAddress;
        }

        setIsConnecting(true);
        try {
          const res = await connectAsync({
            chainId: chain.chainId,
            connector: connector,
          });
          console.log("useEthWalletConnection: connected , res", res);

          const connectedAddr = res.accounts[0] ?? wagmiAddress;
          if (!connectedAddr) {
            throw new Error("Address not found");
          }

          let _chainId = await safeWeb3Operation(
            () => connector.getChainId(),
            chain.chainId,
            "Get chain ID"
          );

          console.log("useEthWalletConnection: chain", _chainId);
          if (chain.chainId !== _chainId) {
            const switched = await switchChain(chain.chainId, connector);
            if (switched) {
              _chainId = await safeWeb3Operation(
                () => connector.getChainId(),
                chain.chainId,
                "Get chain ID after switch"
              );
            }
          }

          if (chain.chainId !== _chainId) {
            setAddress(connectedAddr);
            throw new Error("Chain network not matched, please enable chain");
          }

          setAddress(connectedAddr);
          await reconnect();
          await syncWalletState();
          return connectedAddr as string;
        } catch (e: any) {
          // Type guard function to check if the error is an instance of ConnectorAlreadyConnectedError
          const isConnectorAlreadyConnectedError = (
            error: any
          ): error is ConnectorAlreadyConnectedError => {
            return error instanceof ConnectorAlreadyConnectedError;
          };

          // Type guard function to check if the error is an instance of UserRejectedRequestError
          const isUserRejectedRequestError = (
            error: any
          ): error is UserRejectedRequestError => {
            return error instanceof UserRejectedRequestError;
          };

          if (isConnectorAlreadyConnectedError(e)) {
            let _currentAddress =
              wagmiAddress ??
              connectedAddress ??
              addressRef.current ??
              (await getConnectorAddress());

            if (!_currentAddress) {
              _currentAddress = await syncWalletState();
            }

            if (!_currentAddress) {
              throw new Error("Address not found");
            }

            setAddress(_currentAddress);
            return _currentAddress;
          } else if (isUserRejectedRequestError(e)) {
            console.log(
              "useEthWalletConnection: connect, user rejected",
              e.shortMessage
            );
            throw new Error("User rejected request");
          } else {
            console.error("useEthWalletConnection: connect, error", e);

            // Check if this is a connection error that should trigger disconnect
            const isConnectionError =
              e?.message &&
              !e.message.includes("User rejected") &&
              (e.message.includes("could not coalesce error") ||
                e.message.includes("Failed to connect") ||
                e.message.includes("missing trie node") ||
                e.message.includes("Internal JSON-RPC error") ||
                e.code === -32603 ||
                e.code === -32000 ||
                e.code === "UNKNOWN_ERROR");

            if (isConnectionError) {
              console.warn(
                "Connection error detected, triggering wallet disconnect"
              );

              // Show notification via custom event (will be caught by GlobalErrorHandler)
              window.dispatchEvent(
                new CustomEvent("app-error", {
                  detail: {
                    message:
                      "Wallet connection error. Wallet disconnected. Please reconnect your wallet",
                    context: "Connection Error",
                    isWalletError: true,
                  },
                })
              );

              // Also trigger wallet disconnect event for backward compatibility
              window.dispatchEvent(
                new CustomEvent("wallet-disconnect-required", {
                  detail: {
                    error: e,
                    reason: "Connection error during wallet connect",
                  },
                })
              );
            }

            throw new Error(e.message);
          }
        } finally {
          setIsConnecting(false);
        }
      } else {
        console.error("useEthWalletConnection: connect, Connector not found");
        throw new Error("Connector not found");
      }
    },
    [
      connectors,
      currentConnector,
      connectAsync,
      chain.chainId,
      getConnectorAddress,
      setAddress,
      switchChain,
      wagmiAddress,
      wagmiIsConnected,
      isConnecting,
      connectedAddress,
      syncWalletState,
    ]
  );

  const displayAddress = connectedAddress ?? wagmiAddress ?? addressRef.current;
  const isWalletConnected = !!displayAddress;

  return {
    isConnected: isWalletConnected,
    walletName: currentConnector?.name,
    walletNames: wallets,
    account: addressRef,
    hasAddress: isWalletConnected,
    connect: connect,
    reconnect: async () => {
      // Reconnect disabled - users must manually connect
      console.warn(
        "Auto-reconnect disabled. Users must manually connect wallets."
      );
    },
    disconnect: disconnect,
    selectedConnector: currentConnector,
    switchChain: switchChain,
  };
};

export default useEthWalletConnection;
